import { Button } from '@/components/ui/button'
import { DialogPattern } from '@/components/ui/dialog-pattern'
import { CircleNotchIcon, WarningIcon } from '@phosphor-icons/react'

interface UnsavedDescriptionDialogProps {
  open: boolean
  /** Dismiss (keep editing / stay). */
  onKeepEditing: () => void
  /** Leave without saving the description edit. */
  onDiscard: () => void
  /** Persist the description edit, then leave. */
  onSaveAndClose: () => void
  /** Whether the save is currently in flight (shows a spinner, disables actions). */
  isSaving?: boolean
}

/**
 * Shared "unsaved description edit" confirmation. Used both by the per-panel
 * close/route guard and the app-level task-switch guard so the copy and layout
 * stay identical. Dismissal (Escape/overlay) is treated as "keep editing".
 *
 * Three outcomes (keep editing / discard / save), so this is a DialogPattern
 * with a custom footer rather than a ConfirmDialog.
 */
export function UnsavedDescriptionDialog({
  open,
  onKeepEditing,
  onDiscard,
  onSaveAndClose,
  isSaving = false,
}: UnsavedDescriptionDialogProps) {
  return (
    <DialogPattern
      open={open}
      onOpenChange={next => {
        // Don't allow dismissal mid-save; otherwise treat it as "keep editing".
        if (!next && !isSaving) onKeepEditing()
      }}
      title="You have unsaved changes"
      description="Your description edit hasn't been saved. What would you like to do?"
      icon={<WarningIcon className="h-4 w-4 text-amber-500" />}
      iconClassName="bg-amber-500/10"
      hideCloseButton={isSaving}
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onKeepEditing}
            disabled={isSaving}
          >
            Keep editing
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDiscard}
            disabled={isSaving}
          >
            Discard
          </Button>
          <Button
            type="button"
            size="sm"
            className="min-w-[100px]"
            onClick={onSaveAndClose}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              'Save & close'
            )}
          </Button>
        </>
      }
    />
  )
}
